import { Bullet, Label, p50, percent, time } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  DateAxis,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { TIME_UNIT, getChartInterval } from "@/helpers/charts";
import { stringOrNumberToDate } from "@/helpers/formatters";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { chartFontSettings, chartInitialSettings, getAxisDateFormat } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class GroupedBarChart extends BaseChart {
  yAxis!: CategoryAxis<AxisRenderer> | DateAxis<AxisRenderer>;
  xAxisPrimary!: ValueAxis<AxisRendererX>;
  xAxisSecondary!: ValueAxis<AxisRendererX>;
  isDateAxis!: boolean;
  interval!: time.ITimeInterval;

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    const series = [this.settings.xAxisPrimary, this.settings.xAxisSecondary];

    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;

    if (this.isDateAxis) {
      this.interval = getChartInterval(this.settings.yAxis) as time.ITimeInterval;
    }

    this.createChart();
    this.createAxes();
    this.createCursor();

    series.forEach((name, index) => {
      this.createSeries(name, index);
    });

    this.showLegend(series, (this.chart as XYChart).series.values as LegendData);
  }

  createSeries(name: string, index: number) {
    const s: ColumnSeries = this.isDateAxis
      ? (this.chart as XYChart).series.push(
          ColumnSeries.new(this.root, {
            name: name,
            xAxis: index ? this.xAxisSecondary : this.xAxisPrimary,
            yAxis: this.yAxis,
            valueXField: name,
            valueYField: this.settings.yAxis,
            fill: this.getSeriesColor(index),
          })
        )
      : (this.chart as XYChart).series.push(
          ColumnSeries.new(this.root, {
            name: name,
            xAxis: index ? this.xAxisSecondary : this.xAxisPrimary,
            yAxis: this.yAxis,
            valueXField: name,
            categoryYField: this.settings.yAxis,
            fill: this.getSeriesColor(index),
          })
        );

    this.createTooltip(s as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = s.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        const yField = this.isDateAxis ? s.get("valueYField") : s.get("categoryYField");
        forIn(tooltipValues, (value, key) => {
          if (yField === key || s.get("valueXField") === key) {
            param.push({ key, value });
          }
        });
      }

      return param;
    });

    if (this.settings.showDataLabels) {
      const metricFormatter = this.formatters?.[name];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);
      const axis = s.get("xAxis") as ValueAxis<AxisRendererX>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      s.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerY: p50,
          dx: 8,
          centerX: percent(0),
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueX");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueX");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, {
          locationX: 1,
          sprite: label,
        });
      });
    }

    this.createLegendLabel(s, name);
    this.updateSeriesData(name, s);
  }

  createChart() {
    const chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        paddingTop: 0,
        paddingRight: 20,
        panX: true,
        panY: false,
        wheelY: "zoomX",
        layout: this.layout,
      })
    );

    this.chart = chart;
  }

  createCursor() {
    const cursor = (this.chart as XYChart).set("cursor", XYCursor.new(this.root, {}));
    cursor.lineY.set("forceHidden", true);
    cursor.lineX.set("forceHidden", true);
  }

  createAxes() {
    const xRendererPrimary = AxisRendererX.new(this.root, { cellStartLocation: 0.1, cellEndLocation: 0.9 });
    xRendererPrimary.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.xAxisPrimary = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: xRendererPrimary,
        min: 0,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.xAxisPrimary, this.data),

        marginTop: axisTitle.marginTop,
      })
    );

    const xRendererSecondary = AxisRendererX.new(this.root, {
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      opposite: true,
    });
    xRendererSecondary.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.xAxisSecondary = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: xRendererSecondary,
        min: 0,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.xAxisSecondary, this.data),
        marginBottom: 10,
      })
    );

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.yAxis = this.isDateAxis
      ? (this.chart as XYChart).yAxes.push(
          DateAxis.new(this.root, {
            renderer: yRenderer,
            groupData: true,
            groupIntervals: [this.interval],
            markUnitChange: false,
            baseInterval: { timeUnit: TIME_UNIT.DAY, count: 1 },
            dateFormats: getAxisDateFormat(this.settings.yAxis),
            marginRight: 14,
          })
        )
      : (this.chart as XYChart).yAxes.push(
          CategoryAxis.new(this.root, {
            categoryField: this.settings.yAxis,
            renderer: yRenderer,
            marginRight: 14,
          })
        );

    if (this.settings.yAxisTitle) {
      this.yAxis.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: percent(50),
          marginRight: 10,
        })
      );
    }

    if (this.settings.xAxisTitlePrimary) {
      this.xAxisPrimary.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitlePrimary,
          ...chartFontSettings.axisTitle,
          x: percent(50),
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.xAxisTitleSecondary) {
      this.xAxisSecondary.children.unshift(
        Label.new(this.root, {
          text: this.settings.xAxisTitleSecondary,
          ...chartFontSettings.axisTitle,
          x: percent(50),
          marginBottom: 10,
          centerX: p50,
        })
      );
    }

    this.yAxis.data.setAll(this.data);

    if (this.module === PLAN_MODULE && this.isDateAxis && this.settings.showDataLabels) {
      const yAxis = this.yAxis as DateAxis<AxisRendererY>;
      if (this.settings.yAxis === "week") {
        this.root.events.once("frameended", () => {
          const privateMin = yAxis.getPrivate("min");
          const privateMax = yAxis.getPrivate("max");
          const privateStep = yAxis.getPrivate("step");
          if (
            typeof privateMin === "number" &&
            typeof privateMax === "number" &&
            typeof privateStep === "number" &&
            privateStep > 0
          ) {
            yAxis.set("min", privateMin - privateStep);
            yAxis.set("max", privateMax + privateStep);
            yAxis.set("strictMinMax", true);
          }
        });
      } else if (this.settings.yAxis === "month" || this.settings.yAxis === "quarter") {
        const currentExtraMin = Number(yAxis.get("extraMin") ?? 0);
        const currentExtraMax = Number(yAxis.get("extraMax") ?? 0);
        const requiredExtra = 0.03;
        if (currentExtraMin < requiredExtra) {
          yAxis.set("extraMin", requiredExtra);
        }
        if (currentExtraMax < requiredExtra) {
          yAxis.set("extraMax", requiredExtra);
        }
      }
    }
  }

  mapData(data: ChartDataRecord[]) {
    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      this.module === PLAN_MODULE &&
      this.chartFamily !== FAMILY.OVERTIME &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      const decompositionMetric = this.settings.yAxis;
      const displayMetric = this.settings.xAxisPrimary;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    preparedData = preparedData.map((x) => ({
      [this.settings.yAxis]: x[this.settings.yAxis],
      [this.settings.xAxisPrimary]: x[this.settings.xAxisPrimary] ?? 0,
      [this.settings.xAxisSecondary]: x[this.settings.xAxisSecondary] ?? 0,
    }));
    this.isDateAxis = this.chartFamily === FAMILY.OVERTIME;

    if (!this.isDateAxis) {
      preparedData = orderBy(
        preparedData,
        [this.settings.xAxisPrimary, this.settings.xAxisSecondary],
        ["asc", "asc"]
      ) as ChartDataRecord[];

      preparedData = this.showItems(
        preparedData as ChartDataRecord[],
        this.settings.yAxis,
        this.settings.xAxisPrimary,
        "first"
      );
    }

    return preparedData;
  }

  updateSeriesData(seriesName: string, series: ColumnSeries): void {
    const data = this.isDateAxis
      ? this.data.map((el: ChartDataRecord) => ({
          [this.settings.yAxis]: stringOrNumberToDate(el[this.settings.yAxis] as string).getTime(),
          [seriesName]: el[seriesName],
        }))
      : this.data;

    series.data.setAll(data);
  }
}
